import React, { useState, useEffect } from "react";
import { Button, Card, Select, Row, Col, Typography, Spin, Alert, Table, Space, Tabs, Popconfirm, Progress } from "antd";
import { DownloadOutlined, FileTextOutlined, FolderOpenOutlined, DeleteOutlined, DatabaseOutlined, CloudUploadOutlined } from "@ant-design/icons";
import { request, token } from "../../functions/request";
import { Content } from "antd/es/layout/layout";
import { toast } from "react-toastify";

const { Title, Text } = Typography;

export default function ComexImportExport() {
  const [fromYear, setFromYear] = useState(new Date().getFullYear());
  const [fromMonth, setFromMonth] = useState(1);
  const [toYear, setToYear] = useState(new Date().getFullYear());
  const [toMonth, setToMonth] = useState(new Date().getMonth() + 1);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [existingFiles, setExistingFiles] = useState(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState(null);

  // Gerar anos de 1997 até o ano atual
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1997 + 1 }, (_, i) => 1997 + i);

  const months = [
    { value: 1, label: "Janeiro" },
    { value: 2, label: "Fevereiro" },
    { value: 3, label: "Março" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Maio" },
    { value: 6, label: "Junho" },
    { value: 7, label: "Julho" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Setembro" },
    { value: 10, label: "Outubro" },
    { value: 11, label: "Novembro" },
    { value: 12, label: "Dezembro" },
  ];

  const handleGenerateReport = async () => {
    // Validação
    if (fromYear > toYear || (fromYear === toYear && fromMonth > toMonth)) {
      toast.error("O período inicial não pode ser maior que o período final!");
      return;
    }

    setIsLoading(true);
    setReportData(null);

    try {
      const response = await request("/comexstat/to-csv", {
        method: "POST",
        body: JSON.stringify({
          fromYear: toYear,
          fromMonth: toMonth,
          toYear: fromYear,
          toMonth: fromMonth,
        }),
      });

      if (response && response.files) {
        setReportData(response);
        toast.success("Relatório gerado com sucesso!");
      } else {
        toast.error("Erro ao gerar relatório. Tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao gerar relatório:", error);
      toast.error("Erro ao gerar relatório. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExistingFiles = async () => {
    setIsLoadingFiles(true);
    try {
      const response = await request("/comexstat/csv-files", {
        method: "GET",
      });

      if (response && response.files) {
        setExistingFiles(response);
      } else {
        toast.error("Erro ao buscar arquivos existentes.");
      }
    } catch (error) {
      console.error("Erro ao buscar arquivos:", error);
      toast.error("Erro ao buscar arquivos existentes.");
    } finally {
      setIsLoadingFiles(false);
    }
  };

  useEffect(() => {
    fetchExistingFiles();
  }, []);

  const handleDelete = async (fileName) => {
    try {
      const response = await request(`/comexstat/csv-files/${fileName}`, {
        method: "DELETE",
      });

      if (response) {
        toast.success(`Arquivo ${fileName} deletado com sucesso!`);
        // Atualizar a lista após deletar
        fetchExistingFiles();
      } else {
        toast.error("Erro ao deletar arquivo.");
      }
    } catch (error) {
      console.error("Erro ao deletar arquivo:", error);
      toast.error("Erro ao deletar arquivo. Tente novamente.");
    }
  };

  const handleDownload = async (fileName) => {
    try {
      // Para download de arquivos, usar fetch diretamente com blob
      const response = await fetch(`${process.env.REACT_APP_API_RB}/comexstat/csv-files/${fileName}`, {
        method: "GET",
        headers: {
          "x-access-token": token,
        },
      });

      if (!response.ok) {
        throw new Error(`Erro ao baixar arquivo: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Download de ${fileName} iniciado!`);
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      toast.error('Erro ao baixar arquivo. Tente novamente.');
    }
  };

  // Polling: verifica periodicamente se o servidor voltou a responder após queda de conexão
  const pollForImportCompletion = async (importStartTime, progressInterval, elapsedInterval) => {
    const MAX_POLL_DURATION = 20 * 60 * 1000; // 20 minutos máximo de polling
    const POLL_INTERVAL = 15000; // 15 segundos entre cada tentativa

    while (Date.now() - importStartTime < MAX_POLL_DURATION) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));

      const elapsed = Date.now() - importStartTime;
      setImportProgress(Math.min(50 + ((elapsed / MAX_POLL_DURATION) * 50), 99));

      try {
        const pollCtrl = new AbortController();
        const pollTimeout = setTimeout(() => pollCtrl.abort(), 10000);

        const resp = await fetch(`${process.env.REACT_APP_API_RB}/comexstat/csv-files`, {
          method: "GET",
          headers: { "x-access-token": token },
          signal: pollCtrl.signal,
        });

        clearTimeout(pollTimeout);

        if (resp.ok) {
          // Servidor está respondendo novamente — importação provavelmente concluiu
          clearInterval(progressInterval);
          clearInterval(elapsedInterval);
          return true;
        }
      } catch {
        // Servidor ainda ocupado processando a importação, continuar polling
        const minutes = Math.floor(elapsed / 60000);
        setImportStatus((prev) => ({
          ...prev,
          message: `Servidor ainda processando a importação... (${minutes}min decorridos). Aguardando conclusão...`,
        }));
      }
    }

    clearInterval(progressInterval);
    clearInterval(elapsedInterval);
    return false;
  };

  const handleImportToMSSQL = async (fileName) => {
    // Remove qualquer preloader, polling ou espera de resposta da API
    toast.info("Aguarde de 10 a 30 minutos a finalização da importação.");
    try {
      fetch(`${process.env.REACT_APP_API_RB}/comexstat/csv-to-mssql`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify({
          tableName: "COMEXSTAT_DADOS",
          fileName: fileName,
          filePath: `${process.env.REACT_APP_API_RB}/comexstat/csv-files/${fileName}`,
        }),
      });
    } catch (e) {
      // Não faz nada, pois não queremos mostrar erro imediato
    }
  };

  const handleDeleteMSSQLRecords = async () => {
    // Validação
    if (fromYear > toYear || (fromYear === toYear && fromMonth > toMonth)) {
      toast.error("O período inicial não pode ser maior que o período final!");
      return;
    }

    setIsDeleting(true);
    setDeleteStatus(null);

    try {
      const response = await request("/comexstat/mssql-records", {
        method: "DELETE",
        body: JSON.stringify({
          fromYear: fromYear,
          fromMonth: fromMonth,
          toYear: toYear,
          toMonth: toMonth,
        }),
      });

      if (response) {
        const deletedRows = response.deletedRows || 0;
        
        if (deletedRows === 0) {
          // Nenhum registro encontrado para deletar
          setDeleteStatus({
            type: "warning",
            message: response.message || "Nenhum registro encontrado para deletar",
            tableName: response.tableName,
            period: response.period,
            deletedRows: deletedRows,
          });
          toast.warning("Nenhum registro encontrado no período especificado");
        } else {
          // Registros deletados com sucesso
          setDeleteStatus({
            type: "success",
            message: response.message || "Registros deletados com sucesso",
            tableName: response.tableName,
            period: response.period,
            deletedRows: deletedRows,
          });
          toast.success(`${deletedRows} registro(s) deletado(s) com sucesso!`);
        }
      } else {
        setDeleteStatus({
          type: "error",
          message: "Erro ao deletar registros do MSSQL",
          details: "Resposta inválida do servidor",
        });
        toast.error("Erro ao deletar registros do MSSQL");
      }
    } catch (error) {
      console.error("Erro ao deletar registros do MSSQL:", error);
      setDeleteStatus({
        type: "error",
        message: "Erro ao deletar registros do MSSQL",
        details: error.message,
      });
      toast.error("Erro ao deletar registros do MSSQL. Tente novamente.");
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      title: "Ano",
      dataIndex: "year",
      key: "year",
      width: 100,
    },
    {
      title: "Arquivo",
      dataIndex: "fileName",
      key: "fileName",
    },
    {
      title: "Total de Registros",
      dataIndex: "rows",
      key: "rows",
      width: 150,
      render: (value) => value.toLocaleString("pt-BR"),
    },
    {
      title: "Ações",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record.fileName)}
        >
          Download
        </Button>
      ),
    },
  ];

  const dataSource = reportData?.files.map((file, index) => ({
    key: index,
    year: file.year,
    fileName: file.file.split("\\").pop(),
    rows: file.rows,
  }));

  const existingFilesColumns = [
    {
      title: "Ano",
      dataIndex: "year",
      key: "year",
      width: 100,
    },
    {
      title: "Arquivo",
      dataIndex: "filename",
      key: "filename",
    },
    {
      title: "Tamanho",
      dataIndex: "sizeFormatted",
      key: "sizeFormatted",
      width: 120,
    },
    {
      title: "Ações",
      key: "actions",
      width: 350,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record.filename)}
          >
            Download
          </Button>
          <Button
            type="default"
            icon={<DatabaseOutlined />}
            onClick={() => handleImportToMSSQL(record.filename)}
            loading={isImporting && selectedFile === record.filename}
          >
            Importar MSSQL
          </Button>
          <Popconfirm
            title="Deletar arquivo"
            description={`Tem certeza que deseja deletar ${record.filename}?`}
            onConfirm={() => handleDelete(record.filename)}
            okText="Sim"
            cancelText="Não"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            >
              Deletar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const existingFilesDataSource = existingFiles?.files.map((file, index) => ({
    key: index,
    year: file.year,
    filename: file.filename,
    sizeFormatted: file.sizeFormatted,
  }));

  return (
    <Content style={{ padding: "24px" }}>
      <Card>
        <Title level={3}>
          <FileTextOutlined /> Comex Import/Export - Gerador de Relatórios
        </Title>
        <Text type="secondary">
          Selecione o período desejado para gerar os relatórios de importação e exportação.
        </Text>

        <Tabs
          defaultActiveKey="1"
          items={[
            {
              key: "1",
              label: (
                <span>
                  <FileTextOutlined /> Gerar Relatório
                </span>
              ),
              children: (
                <>
                  <Row gutter={16} style={{ marginTop: 24 }}>
                    <Col span={12}>
                      <Card title="Período Inicial" size="small">
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <div>
                            <Text strong>Ano:</Text>
                            <Select
                              style={{ width: "100%", marginTop: 8 }}
                              value={fromYear}
                              onChange={setFromYear}
                              disabled={isLoading}
                            >
                              {years.map((year) => (
                                <Select.Option key={year} value={year}>
                                  {year}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <Text strong>Mês:</Text>
                            <Select
                              style={{ width: "100%", marginTop: 8 }}
                              value={fromMonth}
                              onChange={setFromMonth}
                              disabled={isLoading}
                            >
                              {months.map((month) => (
                                <Select.Option key={month.value} value={month.value}>
                                  {month.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                        </Space>
                      </Card>
                    </Col>

                    <Col span={12}>
                      <Card title="Período Final" size="small">
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <div>
                            <Text strong>Ano:</Text>
                            <Select
                              style={{ width: "100%", marginTop: 8 }}
                              value={toYear}
                              onChange={setToYear}
                              disabled={isLoading}
                            >
                              {years.map((year) => (
                                <Select.Option key={year} value={year}>
                                  {year}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <Text strong>Mês:</Text>
                            <Select
                              style={{ width: "100%", marginTop: 8 }}
                              value={toMonth}
                              onChange={setToMonth}
                              disabled={isLoading}
                            >
                              {months.map((month) => (
                                <Select.Option key={month.value} value={month.value}>
                                  {month.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                        </Space>
                      </Card>
                    </Col>
                  </Row>

                  <div style={{ textAlign: "center", marginTop: 24 }}>
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleGenerateReport}
                      loading={isLoading}
                      icon={<FileTextOutlined />}
                    >
                      {isLoading ? "Gerando Relatório..." : "Gerar Relatório"}
                    </Button>
                  </div>

                  {isLoading && (
                    <div style={{ textAlign: "center", marginTop: 24 }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 16 }}>
                        <Text>Processando dados... Isso pode levar alguns instantes.</Text>
                      </div>
                    </div>
                  )}

                  {reportData && !isLoading && (
                    <div style={{ marginTop: 24 }}>
                      <Alert
                        message="Relatório Gerado com Sucesso!"
                        description={`Total de ${reportData.totalRows.toLocaleString("pt-BR")} registros processados.`}
                        type="success"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />

                      <Title level={4}>Arquivos Gerados:</Title>
                      <Table
                        columns={columns}
                        dataSource={dataSource}
                        pagination={false}
                        size="middle"
                      />
                    </div>
                  )}
                </>
              ),
            },
            {
              key: "2",
              label: (
                <span>
                  <FolderOpenOutlined /> Arquivos Existentes
                </span>
              ),
              children: (
                <>
                  <div style={{ marginTop: 24, marginBottom: 16 }}>
                    <Space>
                      <Button
                        icon={<FolderOpenOutlined />}
                        onClick={fetchExistingFiles}
                        loading={isLoadingFiles}
                      >
                        Atualizar Lista
                      </Button>
                      {existingFiles && (
                        <Alert
                          message={`${existingFiles.files?.length || 0} arquivo(s) encontrado(s) - Total: ${existingFiles.totalSizeFormatted}`}
                          type="info"
                          showIcon
                        />
                      )}
                    </Space>
                  </div>

                  {isLoadingFiles ? (
                    <div style={{ textAlign: "center", marginTop: 24 }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 16 }}>
                        <Text>Carregando arquivos...</Text>
                      </div>
                    </div>
                  ) : existingFiles && existingFiles.files && existingFiles.files.length > 0 ? (
                    <Table
                      columns={existingFilesColumns}
                      dataSource={existingFilesDataSource}
                      pagination={false}
                      size="middle"
                    />
                  ) : (
                    <Alert
                      message="Nenhum arquivo encontrado"
                      description="Gere um relatório na aba 'Gerar Relatório' para criar novos arquivos."
                      type="warning"
                      showIcon
                    />
                  )}
                </>
              ),
            },
            {
              key: "3",
              label: (
                <span>
                  <CloudUploadOutlined /> Enviar ao MSSQL
                </span>
              ),
              children: (
                <>
                  <div style={{ marginTop: 24, marginBottom: 16 }}>
                    <Alert
                      message="Importar arquivos CSV para o banco de dados MSSQL"
                      description="Selecione um arquivo da lista abaixo e clique em 'Importar MSSQL' para enviar os dados ao banco de dados."
                      type="info"
                      showIcon
                    />
                  </div>

                  {importStatus && (
                    <Alert
                      message={importStatus.message}
                      description={
                        <div>
                          {importStatus.tableName && (
                            <p><strong>Tabela:</strong> {importStatus.tableName}</p>
                          )}
                          {importStatus.totalInserted !== undefined && (
                            <p><strong>Total de registros inseridos:</strong> {importStatus.totalInserted.toLocaleString("pt-BR")}</p>
                          )}
                          {importStatus.filesProcessed !== undefined && (
                            <p><strong>Arquivos processados:</strong> {importStatus.filesProcessed}</p>
                          )}
                          {importStatus.results && importStatus.results.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <strong>Detalhes dos arquivos:</strong>
                              <ul style={{ marginTop: 4, marginBottom: 0 }}>
                                {importStatus.results.map((result, idx) => (
                                  <li key={idx}>
                                    <strong>{result.file}</strong> (Ano: {result.year})
                                    {result.error ? (
                                      <span style={{ color: '#ff4d4f' }}> - Erro: {result.error}</span>
                                    ) : (
                                      <span style={{ color: '#52c41a' }}> - {result.rows} registro(s) inserido(s)</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {importStatus.details && <p style={{ marginTop: 8 }}>{importStatus.details}</p>}
                          {isImporting && (
                            <Progress
                              percent={importProgress}
                              status="active"
                              style={{ marginTop: 16 }}
                            />
                          )}
                        </div>
                      }
                      type={importStatus.type}
                      showIcon
                      closable
                      onClose={() => setImportStatus(null)}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  <div style={{ marginTop: 24, marginBottom: 16 }}>
                    <Button
                      icon={<FolderOpenOutlined />}
                      onClick={fetchExistingFiles}
                      loading={isLoadingFiles}
                    >
                      Atualizar Lista
                    </Button>
                  </div>

                  {isLoadingFiles ? (
                    <div style={{ textAlign: "center", marginTop: 24 }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 16 }}>
                        <Text>Carregando arquivos...</Text>
                      </div>
                    </div>
                  ) : existingFiles && existingFiles.files && existingFiles.files.length > 0 ? (
                    <Table
                      columns={existingFilesColumns}
                      dataSource={existingFilesDataSource}
                      pagination={false}
                      size="middle"
                    />
                  ) : (
                    <Alert
                      message="Nenhum arquivo encontrado"
                      description="Gere um relatório na aba 'Gerar Relatório' para criar novos arquivos."
                      type="warning"
                      showIcon
                    />
                  )}
                </>
              ),
            },
            {
              key: "4",
              label: (
                <span>
                  <DeleteOutlined /> Deletar Registros MSSQL
                </span>
              ),
              children: (
                <>
                  <div style={{ marginTop: 24, marginBottom: 16 }}>
                    <Alert
                      message="ATENÇÃO: Operação Irreversível!"
                      description="Esta operação irá deletar permanentemente os registros do banco de dados MSSQL no período selecionado. Use com cuidado!"
                      type="warning"
                      showIcon
                    />
                  </div>

                  {deleteStatus && (
                    <Alert
                      message={deleteStatus.message}
                      description={
                        <div>
                          {deleteStatus.tableName && (
                            <p><strong>Tabela:</strong> {deleteStatus.tableName}</p>
                          )}
                          {deleteStatus.deletedRows !== undefined && (
                            <p>
                              <strong>Registros deletados:</strong> {deleteStatus.deletedRows.toLocaleString("pt-BR")}
                            </p>
                          )}
                          {deleteStatus.period && (
                            <p>
                              <strong>Período:</strong> {deleteStatus.period.from.month}/{deleteStatus.period.from.year} até {deleteStatus.period.to.month}/{deleteStatus.period.to.year}
                            </p>
                          )}
                          {deleteStatus.details && <p>{deleteStatus.details}</p>}
                        </div>
                      }
                      type={deleteStatus.type}
                      showIcon
                      closable
                      onClose={() => setDeleteStatus(null)}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  <Row gutter={16} style={{ marginTop: 24 }}>
                    <Col span={12}>
                      <Card title="Período Inicial" size="small">
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <div>
                            <Text strong>Ano:</Text>
                            <Select
                              style={{ width: "100%", marginTop: 8 }}
                              value={fromYear}
                              onChange={setFromYear}
                              disabled={isDeleting}
                            >
                              {years.map((year) => (
                                <Select.Option key={year} value={year}>
                                  {year}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <Text strong>Mês:</Text>
                            <Select
                              style={{ width: "100%", marginTop: 8 }}
                              value={fromMonth}
                              onChange={setFromMonth}
                              disabled={isDeleting}
                            >
                              {months.map((month) => (
                                <Select.Option key={month.value} value={month.value}>
                                  {month.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                        </Space>
                      </Card>
                    </Col>

                    <Col span={12}>
                      <Card title="Período Final" size="small">
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <div>
                            <Text strong>Ano:</Text>
                            <Select
                              style={{ width: "100%", marginTop: 8 }}
                              value={toYear}
                              onChange={setToYear}
                              disabled={isDeleting}
                            >
                              {years.map((year) => (
                                <Select.Option key={year} value={year}>
                                  {year}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <Text strong>Mês:</Text>
                            <Select
                              style={{ width: "100%", marginTop: 8 }}
                              value={toMonth}
                              onChange={setToMonth}
                              disabled={isDeleting}
                            >
                              {months.map((month) => (
                                <Select.Option key={month.value} value={month.value}>
                                  {month.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </div>
                        </Space>
                      </Card>
                    </Col>
                  </Row>

                  <div style={{ textAlign: "center", marginTop: 24 }}>
                    <Popconfirm
                      title="Deletar registros do MSSQL"
                      description={
                        <div style={{ maxWidth: 300 }}>
                          <p>Tem certeza que deseja deletar os registros do período:</p>
                          <p><strong>{fromMonth}/{fromYear} até {toMonth}/{toYear}</strong></p>
                          <p style={{ color: 'red' }}>Esta ação não pode ser desfeita!</p>
                        </div>
                      }
                      onConfirm={handleDeleteMSSQLRecords}
                      okText="Sim, deletar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        danger
                        type="primary"
                        size="large"
                        loading={isDeleting}
                        icon={<DeleteOutlined />}
                      >
                        {isDeleting ? "Deletando Registros..." : "Deletar Registros do MSSQL"}
                      </Button>
                    </Popconfirm>
                  </div>

                  {isDeleting && (
                    <div style={{ textAlign: "center", marginTop: 24 }}>
                      <Spin size="large" />
                      <div style={{ marginTop: 16 }}>
                        <Text>Deletando registros... Isso pode levar alguns instantes.</Text>
                      </div>
                    </div>
                  )}
                </>
              ),
            },
          ]}
        />
      </Card>
    </Content>
  );
}
